/* eslint-disable @typescript-eslint/no-shadow */
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isPlainObject } from 'remeda'
import { useExchangeRates } from '@components/ExchangeRatesProvider'
import { QuickAdd } from '@components/QuickAdd/QuickAdd'
import { useStaffPickImage } from '@components/StaffPickImageProvider'
import { PlusIconSmall } from '@velasca/icons/plus-small'
import type { StaffPickIcon, Variant } from '@velasca/types'
import { ConditionalLink } from '@velasca/ui/conditional-link'
import { HorizontalCardDescription } from '@velasca/ui/horizontal-card-description'
import { HorizontalCardPrice } from '@velasca/ui/horizontal-card-price'
import { HorizontalCardTitle } from '@velasca/ui/horizontal-card-title'
import { PriceRow } from '@velasca/ui/price-row'
import { SansText } from '@velasca/ui/sans-text'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { handleError } from '@velasca/utils/handle-error'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { QuickAddSimpleProductButton } from './QuickAddSimpleProductButton'
import {
  Background,
  Button,
  Card,
  Details,
  ImageWrapper,
  ProductImage,
  Separator,
} from './SuggestedHorizontalProduct.styles'
import type { SuggestedHorizontalProductProps } from './SuggestedHorizontalProduct.types'

export const SuggestedHorizontalProduct = ({
  title,
  productType,
  loading,
  variant,
  isGiftCard,
  sizes,
  shouldOpenCartDrawerOnAddToCart,
  isQuickAdd = true,
  onAddToCart,
  onQuickAdd,
}: SuggestedHorizontalProductProps): JSX.Element => {
  const { t: globalT } = useTranslation('global')
  const { t } = useTranslation('cartDrawer')
  const [isQuickAddPopupOpen, setIsQuickAddPopupOpen] = useState<boolean>(false)

  const { convert } = useExchangeRates()

  const price = useMemo(() => {
    const minPrice = isPlainObject(variant.price) ? variant.price?.min * 100 : 0
    const maxPrice = isPlainObject(variant.price) ? variant.price?.max * 100 : 0

    if (minPrice !== maxPrice) {
      return `${convert(minPrice)} - ${convert(maxPrice)}`
    }

    return convert(maxPrice)
  }, [variant, convert])

  const compareAtPrice = useMemo(() => {
    const minPrice = isPlainObject(variant?.compareAtPrice)
      ? variant?.compareAtPrice?.min * 100
      : 0
    const maxPrice = isPlainObject(variant?.compareAtPrice)
      ? variant?.compareAtPrice?.max * 100
      : 0

    if (minPrice !== maxPrice) {
      return `${convert(minPrice)} - ${convert(maxPrice)}`
    }

    return convert(maxPrice)
  }, [variant, convert])

  const isPriceDiscounted = useMemo(() => {
    const nomalizedCompareAtPrice = isPlainObject(variant?.compareAtPrice)
      ? variant?.compareAtPrice?.min
      : 0
    const nomalizedPrice = isPlainObject(variant?.price)
      ? variant?.price?.max
      : 0

    return nomalizedCompareAtPrice > nomalizedPrice
  }, [variant])

  // SHOP-3508: Refactoring useStaffPickIcon
  const { staffPickIcons, settings } = useStaffPickImage()
  const getStaffPickIcon = useCallback(
    (staffPickIconID: Variant['staffPickIconID'], isNew: Variant['isNew']) => {
      if (staffPickIcons && !loading) {
        const currentStaffPickIcon = staffPickIcons.find(
          (staffPickIcon: StaffPickIcon) =>
            staffPickIcon.id === staffPickIconID,
        )

        if (currentStaffPickIcon) {
          return currentStaffPickIcon
        }

        // If product is not new, there is no point in looking for a new icon
        if (!isNew) {
          return
        }

        if (!settings.newID) {
          handleError(
            new Error(
              'Product is new but there is no newID defined. Define a newID in the CMS',
            ),
          )

          return
        }

        const newIcon = staffPickIcons.find(
          (staffPickIcon: StaffPickIcon) =>
            staffPickIcon?.id === settings.newID,
        )

        if (!newIcon) {
          handleError(
            new Error('No new staff pick icon found, plese add it to cms'),
          )
        }

        return newIcon
      }

      return undefined
    },
    [staffPickIcons, settings, loading],
  )

  const { staffPickIconID, handle, description, img, name, isNew } = variant

  return (
    <>
      <Background>
        <Card>
          <a href={`/products/${handle ?? ''}`}>
            <ImageWrapper>
              {img?.front && (
                <ProductImage
                  lazyload
                  lqip
                  showDefault
                  sources={buildIntrinsicWidthSources({ src: img.front })}
                  draggable={false}
                  transitionDuration={50}
                  imgSrc={setQueryParams(img.front, [
                    { name: 'width', value: '30' },
                  ])}
                  alt={t('cart_drawer_product_alt_template', {
                    color: description?.bold,
                    material: description?.thin,
                    productTitle: title,
                    productType,
                  })}
                />
              )}
            </ImageWrapper>
          </a>

          <Details>
            <ConditionalLink
              condition={Boolean(handle)}
              href={`/products/${handle}`}
            >
              <HorizontalCardTitle>{title}</HorizontalCardTitle>
            </ConditionalLink>
            <HorizontalCardDescription>{productType}</HorizontalCardDescription>
            <HorizontalCardDescription>
              {description?.thin}
            </HorizontalCardDescription>
            <PriceRow>
              {price && (
                <HorizontalCardPrice
                  dangerouslySetInnerHTML={{
                    __html: price,
                  }}
                />
              )}
              {isPriceDiscounted && (
                <HorizontalCardPrice
                  strikethrough
                  color="gray"
                  dangerouslySetInnerHTML={{
                    __html: compareAtPrice,
                  }}
                />
              )}
            </PriceRow>
          </Details>
        </Card>

        {isQuickAdd && (
          <>
            <Separator />
            {sizes?.length === 1 ? (
              <QuickAddSimpleProductButton
                size={sizes[0]}
                handle={handle}
                shouldOpenCartDrawerOnAddToCart={
                  shouldOpenCartDrawerOnAddToCart
                }
                onQuickAdd={onQuickAdd}
              />
            ) : (
              <Button
                onClick={() => {
                  setIsQuickAddPopupOpen(true)
                  onQuickAdd && onQuickAdd(handle)
                }}
              >
                <SansText uppercase as="span" size="sm">
                  {globalT('products_commons_choose_size_selection')}
                </SansText>
                <PlusIconSmall width="18" height="18" />
              </Button>
            )}
          </>
        )}
      </Background>

      {isQuickAdd && (
        <QuickAdd
          isPriceDiscounted={isPriceDiscounted}
          compareAtPrice={compareAtPrice}
          staffPickIcon={getStaffPickIcon(staffPickIconID, isNew)}
          title={title || ''}
          price={price}
          productType={productType}
          description={description}
          isGiftCard={isGiftCard}
          sizes={sizes}
          handle={handle}
          isOpen={isQuickAddPopupOpen}
          shouldOpenCartDrawerOnAddToCart={shouldOpenCartDrawerOnAddToCart}
          image={{
            src: img?.front ?? '',
            alt: name ?? '',
          }}
          onAddToCart={onAddToCart}
          onClose={() => {
            setIsQuickAddPopupOpen(false)
          }}
        />
      )}
    </>
  )
}
